import { useQuery } from '@apollo/client';
import { Button, Container, Divider, FlexGrid, Header } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import gql from 'graphql-tag';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import store from 'store';
import 'url-search-params-polyfill';
import useAnalytics from '../../../lib/hooks/useAnalytics';
import useBannerAds from '../../../lib/hooks/useBannerAds';
import useGeoIpInfo from '../../../lib/hooks/useGeoIpInfo';
import usePreferredFuelType from '../../../lib/hooks/usePreferredFuelType';
import useSimpleWalletStatus from '../../../lib/hooks/useSimpleWalletStatus';
import useStationImpressions from '../../../lib/hooks/useStationImpressions';
import { compact, convertToGBApiCountry } from '../../../lib/utils';
import buildSearchParameters from '../../../lib/utils/buildSearchParameters';
import getFuelOption from '../../../lib/utils/getFuelOption';
import { getQueryParamsFromLocation, toQueryString } from '../../../lib/utils/querystring';
import { AD_SLOTS, ANALYTICS_EVENTS, COUNTRY_CODES, FUEL_TYPES } from '../../constants';
import { Ad } from '../Ad';
import GasPriceSearchForm from '../GasPriceSearchForm';
import GenericStationList from '../GenericStationList';
import MiniBrandList from '../MiniBrandList';
import Page from '../Page';
import PriceTrends from '../PriceTrends';
import RegionContainer from '../RegionContainer';
import styles from './SearchResultsPage.module.css';

const cx = classnames.bind(styles);

export const GET_LOCATION = gql`
  query LocationBySearchTerm(
    $brandId: Int,
    $cursor: String,
    $fuel: Int,
    $lang: String,
    $lat: Float,
    $lng: Float,
    $maxAge: Int,
    $search: String,
  ) {
    locationBySearchTerm(lat: $lat, lng: $lng, search: $search, priority: "locality") {
      countryCode
      displayName
      latitude
      longitude
      regionCode
      stations(brandId: $brandId, cursor: $cursor, fuel: $fuel, lat: $lat, lng: $lng, maxAge: $maxAge, priority: "locality") {
        count
        cursor {
          next
        }
        results {
          address {
            country
            line1
            line2
            locality
            postalCode
            region
          }
          badges(lang: $lang) {
            badgeId,
            callToAction,
            campaignId,
            clickTrackingUrl,
            description,
            detailsImageUrl,
            detailsImpressionTrackingUrls,
            imageUrl,
            impressionTrackingUrls,
            targetUrl,
            title,
          }
          brands {
            brandId
            brandingType
            imageUrl
            name
          }
          distance
          emergencyStatus {
            hasDiesel {
              nickname
              reportStatus
              updateDate
            }
            hasGas {
              nickname
              reportStatus
              updateDate
            }
            hasPower {
              nickname
              reportStatus
              updateDate
            }
          }
          enterprise
          fuels
          hasActiveOutage
          id
          isFuelmanSite
          name
          priceUnit
          ratingsCount
          starRating
        }
      }
      trends {
        areaName
        country
        today
        todayLow
        trend
      }
    }
  }
`;

export const GET_SEARCH_PRICES = gql`
  query SearchPrices(
    $brandId: Int,
    $cursor: String,
    $fuel: Int,
    $lat: Float,
    $lng: Float,
    $maxAge: Int,
    $search: String,
  ) {
    locationBySearchTerm(lat: $lat, lng: $lng, search: $search, priority: "locality") {
      displayName
      stations(brandId: $brandId, cursor: $cursor, fuel: $fuel, lat: $lat, lng: $lng, maxAge: $maxAge, priority: "locality") {
        count
        cursor {
          next
        }
        results {
          id
          offers {
            discounts {
              grades
              highlight
              pwgbDiscount
              receiptDiscount
            }
            highlight
            id
            types
            use
          }
          payStatus {
            isPayAvailable
          }
          prices {
            cash {
              nickname
              postedTime
              price
              formattedPrice
            }
            credit {
              nickname
              postedTime
              price
              formattedPrice
            }
            discount
            fuelProduct
          }
        }
      }
    }
  }
`;

export default function SearchResultsPage() {
  const { t, i18n } = useTranslation(['fuel', 'price', 'location']);
  const history = useHistory();
  const location = useLocation();
  const {
    brandId: brandIdParam,
    fuel: fuelOptionValue,
    lat,
    lng,
    maxAge: ageParam,
    method: methodParam,
    search,
  } = getQueryParamsFromLocation(location);
  const lastFuelType = usePreferredFuelType();
  const fuelParam = fuelOptionValue || getFuelOption(lastFuelType).value;

  // Fix formatting of incoming querystring parameters
  const fuel = fuelParam && parseInt(fuelParam, 10);
  const brandId = brandIdParam && parseInt(brandIdParam, 10);
  const maxAge = ageParam && parseInt(ageParam, 10);
  const method = (methodParam || store.get('lastMethod'))?.toLowerCase();

  const { country } = useGeoIpInfo();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const analytics = useAnalytics();
  const shouldSkipQuery = !search && !(lat && lng);

  const walletStatus = useSimpleWalletStatus(shouldSkipQuery);
  const { data, fetchMore, error, loading } = useQuery(GET_LOCATION, {
    skip: shouldSkipQuery,
    variables: {
      brandId,
      fuel,
      lang: i18n.language,
      lat: parseFloat(lat) || undefined,
      lng: parseFloat(lng) || undefined,
      maxAge,
      search,
    },
  });
  const {
    data: pricesData,
    fetchMore: fetchMorePrices,
    loading: pricesLoading,
  } = useQuery(GET_SEARCH_PRICES, {
    ssr: false,
    fetchPolicy: 'no-cache',
    skip: shouldSkipQuery,
    variables: {
      brandId,
      fuel,
      lat: parseFloat(lat) || undefined,
      lng: parseFloat(lng) || undefined,
      maxAge,
      search,
    },
  });
  const resultLocation = data?.locationBySearchTerm;
  const stationResults = resultLocation?.stations;
  const stations = stationResults?.results || [];
  const nextCursor = stationResults?.cursor?.next;
  const count = stationResults?.count;
  const trend = resultLocation?.trends?.[0];
  const { hasPwGBCard, hasPwGBPlusCard, accountStatus: payAccountStatus } = walletStatus || {};

  // Merge price data from client-only query into station objects
  const stationsWithPrices = useMemo(() => {
    const priceStations = pricesData?.locationBySearchTerm?.stations?.results || [];
    const priceMap = new Map(priceStations.map(s => [s.id, s]));

    return stations.map(station => ({
      ...station,
      prices: priceMap.get(station.id)?.prices || [],
      offers: priceMap.get(station.id)?.offers || [],
      payStatus: priceMap.get(station.id)?.payStatus || null,
    }));
  }, [stations, pricesData]);

  const stationsFound = nextCursor && nextCursor !== '0';
  const moreExist = parseInt(nextCursor, 10) < count - 1;
  const desktopBannerName = 'GasBuddy_Search_728x90';
  const mobileBannerName = 'GasBuddy_MobileWeb_320x50_Search';

  const stationIDs = useMemo(() => stations.map(s => s.id), [stations]);
  const [clearStationIds] = useStationImpressions(stationIDs);

  // Handler for search submit
  const handleSubmit = useCallback((formFields) => {
    const newQueryParams = compact(formFields);
    const querystring = toQueryString(newQueryParams);
    const selectedLabel = getFuelOption(formFields.fuel).label;

    analytics.identifyUser({ fuel_type: selectedLabel });

    analytics.tagEvent({
      name: ANALYTICS_EVENTS.STATION_SEARCH,
      attributes: {
        Name: formFields.search,
        type: formFields.lat && formFields.lng ? 'Near Me' : 'String',
        'Fuel Type': formFields.fuel,
        'Brand Id': formFields.brandId || 'All',
        'Max Price Age': formFields.maxAge || 'None specified',
        'Payment Method': formFields.method || 'all',
      },
    });

    store.set('lastFuelType', selectedLabel);
    store.set('lastMethod', formFields.method);

    if (querystring !== location.search.substring(1)) {
      clearStationIds();
      history.push({
        search: `?${querystring}`,
      });
    }
  }, [analytics, clearStationIds, history, location.search]);

  // Handler for "load more" button
  const loadMoreStations = useCallback(() => {
    setIsLoadingMore(true);
    fetchMore({
      variables: {
        cursor: nextCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        setIsLoadingMore(false);
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          locationBySearchTerm: {
            ...prev.locationBySearchTerm,
            stations: {
              count: fetchMoreResult.locationBySearchTerm.stations.count,
              cursor: fetchMoreResult.locationBySearchTerm.stations.cursor,
              results: [
                ...prev.locationBySearchTerm.stations.results,
                ...fetchMoreResult.locationBySearchTerm.stations.results],
            },
          },
        };
      },
    });
    // Also fetch prices for the next page of stations
    fetchMorePrices({
      variables: {
        cursor: nextCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          locationBySearchTerm: {
            ...prev.locationBySearchTerm,
            stations: {
              count: fetchMoreResult.locationBySearchTerm.stations.count,
              cursor: fetchMoreResult.locationBySearchTerm.stations.cursor,
              results: [
                ...prev.locationBySearchTerm.stations.results,
                ...fetchMoreResult.locationBySearchTerm.stations.results],
            },
          },
        };
      },
    });
  }, [fetchMore, fetchMorePrices, nextCursor]);

  const onViewMapClick = useCallback(() => {
    analytics.tagEvent({ name: ANALYTICS_EVENTS.SEARCH_MAP_CLICK });
  }, [analytics]);

  // Generate station list heading and view map link
  const stationListInfo = useMemo(() => {
    const fuelLabel = fuel ? getFuelOption(fuel).label : FUEL_TYPES.REGULAR;
    let viewMapLink = `/gaspricemap?fuel=${fuel}`;

    let descriptor = 'Near';
    let areaName = search;

    if (resultLocation) {
      const { countryCode, displayName, latitude, longitude, regionCode } = resultLocation;
      const zoomLevelForKnownCoords = 13;
      const zoomLevelForUnknownCoords = 7;

      if (!displayName) {
        descriptor = 'Nearby';
      } else if (!latitude && !longitude && regionCode) {
        descriptor = 'In';
      }

      if (displayName) {
        areaName = displayName;
      }

      // build map url
      if (latitude && longitude) { // if we have lat & long from resultLocation, we do not need country
        viewMapLink += `&z=${zoomLevelForKnownCoords}&lat=${latitude}&lng=${longitude}`;
      } else if (countryCode) {
        viewMapLink += `&z=${zoomLevelForUnknownCoords}&country=${convertToGBApiCountry(countryCode)}&search=${encodeURIComponent(areaName)}`;
      }
    }

    // build headingText
    const isNonDiesel = [FUEL_TYPES.REGULAR, FUEL_TYPES.MIDGRADE, FUEL_TYPES.PREMIUM].includes(fuelLabel);
    const headingText = t(
      `${isNonDiesel ? 'nonDiesel' : 'diesel'}Prices${descriptor}`,
      { ns: 'price', fuelName: fuelLabel, areaName },
    );

    return {
      headingText,
      viewMapLink,
    };
  }, [fuel, resultLocation, search, t]);

  const hasFetchedResults = !loading && !error && !!(search?.length || (lat && lng));
  const bannerAds = useBannerAds(desktopBannerName, mobileBannerName);

  const pageMeta = useMemo(() => ({
    Text: search,
    Lat: lat,
    Lng: lng,
  }), [lat, lng, search]);

  return (
    <Page
      name="Station_Search_Results" // updated in v9
      meta={pageMeta}
      bannerAds={bannerAds}
      description={t('searchResultsMetaDescription', { ns: 'price' })}
      showAppBanner
      title={t('searchResultsMetaTitle', { ns: 'price' })}
      withTopAd
    >
      <Container>
        <FlexGrid container>
          <FlexGrid.Column desktop={8} tablet={7}>
            <GasPriceSearchForm
              brand={(brandId && parseInt(brandId, 10))}
              country={country}
              fuel={(fuel && parseInt(fuel, 10))}
              maxAge={(maxAge && parseInt(maxAge, 10))}
              method={method}
              onSubmit={handleSubmit}
              search={search}
            />
            {hasFetchedResults && (
              <div className={cx('summary')}>
                {Object.values(COUNTRY_CODES).includes(resultLocation?.countryCode) && (
                  <Button
                    as="a"
                    className={cx('mapLink')}
                    data-testid="mapLink"
                    href={stationListInfo.viewMapLink}
                    onClick={onViewMapClick}
                    tertiary
                  >
                    {t('viewMap', { ns: 'location' })}
                  </Button>
                )}
                <Header as="h2" className={cx('resultsHeader')}>{stationListInfo.headingText}</Header>
              </div>
            )}
            {error ? (
              <div>
                <em>{t('errorFetchingStations', { ns: 'station' })}</em>
              </div>
            ) : (
              <GenericStationList
                fuelType={fuel || undefined}
                hasPwGBCard={hasPwGBCard}
                hasPwGBPlusCard={hasPwGBPlusCard}
                includeCash={method !== 'credit'}
                includeLoadMoreButton={stationsFound && moreExist}
                isFetchingStations={loading || isLoadingMore}
                isLoadingPrices={pricesLoading}
                loadMoreButtonHref={`/home?${toQueryString(compact({
                  ...buildSearchParameters({ lat, lng, search }),
                  fuel,
                  brandId,
                  maxAge,
                  cursor: nextCursor,
                }))}`}
                loadMoreStations={loadMoreStations}
                locationCountryCode={resultLocation?.countryCode}
                maxAge={(maxAge && parseInt(maxAge, 10))}
                payAccountStatus={payAccountStatus}
                search={search}
                stations={stationsWithPrices}
              />
            )}

            {hasFetchedResults && (
              <Divider className={cx('divider')} />
            )}

            {!!trend && (
              <PriceTrends
                location={trend.areaName}
                trend={trend}
              />
            )}
          </FlexGrid.Column>
          <FlexGrid.Column desktop={4} tablet={5}>
            <Ad
              id={AD_SLOTS[4].slotID}
              path="GasBuddy_Search_300x250"
              sizes={AD_SLOTS[4].sizes}
              sizeMapping={AD_SLOTS[4].sizeMapping}
              collapseIfEmpty
            />
            <br />
            <RegionContainer country={stations[0]?.address?.country || COUNTRY_CODES.USA} />
            <MiniBrandList showHeader />
          </FlexGrid.Column>
        </FlexGrid>
      </Container>
    </Page>
  );
}
